import { FormFieldViewer } from "./form-field-viewer";
/**
 * Ported from the-answer-2 (BACKLOG §16: lift the components, not the app).
 * Behaviour is theirs; the edges are savvy's. Changes made on the way in:
 *   - the field's identity is a stable string `id`, not a client-assigned
 *     `callTypeFormFieldId` number sitting beside an unrelated `id`;
 *   - a section is a flat `group` name, not a nested object with its own id;
 *   - drafts, feature announcements, router state and toasts are dropped —
 *     they belong to the host app, not the component.
 */


import type React from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import type { FieldValue, FormField } from "./types";
import { useState } from "react";

interface FormPreviewProps {
  elements: FormField[];
  formTitle?: string;
}

export function FormPreview({
  elements,
  formTitle = "Form Preview",
}: FormPreviewProps) {
  const [formData, setFormData] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (key: string, value: FieldValue) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

    // Clear error when field is filled
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const newErrors: Record<string, string> = {};
    elements.forEach((element) => {
      if (element.required && !formData[element.key]) {
        newErrors[element.key] = "This field is required";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Nothing is submitted: the preview exists so the operator can try the
    // form the way an agent will, not to record anything.
    setErrors({});
  };

  return (
    <Card className="shadow-md">
      <CardHeader>
        <CardTitle>{formTitle}</CardTitle>
        <CardDescription>Please fill out all required fields</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {elements.map((element) => (
            <div key={element.id} className="space-y-1">
              <FormFieldViewer
                field={element}
                value={formData[element.key] ?? ""}
                onChange={(key, _label, value) => handleChange(key, value)}
              />
              {errors[element.key] && (
                <p className="text-xs text-destructive mt-1">
                  {errors[element.key]}
                </p>
              )}
            </div>
          ))}
        </form>
      </CardContent>
      <CardFooter>
        <Button type="submit" className="w-full" onClick={handleSubmit}>
          Submit
        </Button>
      </CardFooter>
    </Card>
  );
}
